import { startOfToday } from 'date-fns'
import { useState } from 'react'

import { EditEquipmentModal } from '@/components/EquipmentTable'
import { trailerConfig } from '@/components/TrailersTable'
import { truckConfig } from '@/components/TrucksTable'
import { DateInput } from '@/components/ui/DateInput'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import type { Equipment } from '@/models/equipment'
import { PolicyChangeKind } from '@/models/policyChange'

export type VehicleKind = PolicyChangeKind.ADD_TRUCK | PolicyChangeKind.ADD_TRAILER

/** What only the insurance providers need about a new vehicle, kept in the change rather than the roster. */
export interface VehicleExtras {
  lossPayee: string
  garagingAddress: string
  activeDate?: Date
}

interface VehicleModalProps {
  kind: VehicleKind
  item: Equipment
  isNew: boolean
  existing: Equipment[]
  initial?: VehicleExtras
  onClose: () => void
  /** Rejects with the API's error, which the form shows on its fields. */
  onSubmit: (item: Equipment, extras: VehicleExtras) => Promise<void>
}

/** The roster's vehicle form with a new vehicle's loss payee, garaging address and first day running. */
export function VehicleModal({ kind, item, isNew, existing, initial, onClose, onSubmit }: VehicleModalProps) {
  const config = kind === PolicyChangeKind.ADD_TRUCK ? truckConfig : trailerConfig
  const [lossPayee, setLossPayee] = useState(initial?.lossPayee ?? '')
  const [garagingAddress, setGaragingAddress] = useState(initial?.garagingAddress ?? '')
  const [activeDate, setActiveDate] = useState<Date | undefined>(initial?.activeDate)

  return (
    <EditEquipmentModal
      item={item}
      isNew={isNew}
      existingItems={existing}
      onClose={onClose}
      onSubmit={(edited) => onSubmit(edited, { lossPayee, garagingAddress, activeDate })}
      config={config}
      enforceCarrierRequired
    >
      <Field>
        <FieldLabel htmlFor={`${kind}-loss-payee`} optional>
          Loss payee
        </FieldLabel>
        <Input
          id={`${kind}-loss-payee`}
          placeholder="The lender paid if it's totaled"
          value={lossPayee}
          onChange={(event) => setLossPayee(event.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${kind}-garaging`} optional>
          Garaging address
        </FieldLabel>
        <Input
          id={`${kind}-garaging`}
          placeholder="Leave blank for your main address"
          value={garagingAddress}
          onChange={(event) => setGaragingAddress(event.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${kind}-active`} optional>
          Date the {config.label} starts running
        </FieldLabel>
        <DateInput
          value={activeDate}
          onChange={setActiveDate}
          hidden={{ before: startOfToday() }}
          inputProps={{ id: `${kind}-active` }}
        />
      </Field>
    </EditEquipmentModal>
  )
}
